import React, { useState, useMemo, useRef, useEffect } from "react";
import { ICON_LIBRARY, ICON_CATEGORIES, Icon } from "../../lib/icons";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Search, X } from "lucide-react";

/**
 * IconPicker — sélecteur visuel d'icônes avec recherche.
 * Props :
 *  - value (string) : nom de l'icône actuellement sélectionnée (ex: "Phone")
 *  - onChange(name) : callback quand l'utilisateur choisit une icône
 *  - size : "sm" | "md" (bouton)
 *  - allowEmpty : si true, permet de désélectionner (icône null)
 */
export default function IconPicker({ value, onChange, size = "md", allowEmpty = false, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tous");
  const popRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ICON_LIBRARY.filter(i => {
      const matchCat = activeCategory === "Tous" || i.category === activeCategory;
      if (!q) return matchCat;
      return matchCat && (i.name.toLowerCase().includes(q) || i.label.toLowerCase().includes(q));
    });
  }, [query, activeCategory]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const dim = size === "sm" ? "h-9 w-9" : "h-10 w-10";

  return (
    <div className="relative inline-block" ref={popRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={`${dim} flex items-center justify-center rounded-lg border border-[hsl(var(--border))] bg-white hover:border-[color:var(--brand-sky)] hover:bg-[hsl(var(--muted))]/40 transition disabled:opacity-50 disabled:cursor-not-allowed`}
        title={value ? `Icône : ${value}` : "Choisir une icône"}
        data-testid="icon-picker-trigger"
      >
        {value ? (
          <Icon name={value} className="w-5 h-5 text-[color:var(--brand-navy)]" />
        ) : (
          <span className="text-[9px] text-[hsl(var(--muted-foreground))] font-semibold uppercase">Icône</span>
        )}
      </button>

      {open && (
        <div
          className="absolute z-[150] mt-1.5 w-[420px] max-w-[90vw] bg-white rounded-xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden"
          style={{ right: 0 }}
          data-testid="icon-picker-popover"
        >
          {/* Header : search */}
          <div className="p-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher une icône..."
                  className="pl-8 h-9"
                  data-testid="icon-picker-search"
                />
              </div>
              {allowEmpty && value && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-9 px-2"
                  onClick={() => { onChange(""); setOpen(false); }}
                  title="Retirer l'icône"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Categories */}
            <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-thin">
              {["Tous", ...ICON_CATEGORIES].map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full font-semibold transition ${
                    activeCategory === cat
                      ? "bg-[color:var(--brand-navy)] text-white"
                      : "bg-white border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[color:var(--brand-sky)]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div className="max-h-[320px] overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">
                Aucune icône trouvée.
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-1">
                {filtered.map(icon => (
                  <button
                    key={icon.name}
                    type="button"
                    onClick={() => { onChange(icon.name); setOpen(false); setQuery(""); }}
                    className={`aspect-square flex items-center justify-center rounded-md hover:bg-[color:var(--brand-sky)]/10 hover:scale-105 transition border ${
                      value === icon.name ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/10" : "border-transparent"
                    }`}
                    title={`${icon.label} (${icon.name})`}
                    data-testid={`icon-option-${icon.name}`}
                  >
                    <Icon name={icon.name} className="w-4 h-4 text-[color:var(--brand-navy)]" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer info */}
          <div className="px-3 py-2 text-[10px] text-[hsl(var(--muted-foreground))] border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 flex items-center justify-between">
            <span>{filtered.length} icône{filtered.length > 1 ? "s" : ""}</span>
            {value && <span className="font-mono">Sélection : <strong>{value}</strong></span>}
          </div>
        </div>
      )}
    </div>
  );
}
